import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { BOOK_COVER, LINKS, IMAGES } from "@/data/content";

export default function Libro() {
  return (
    <div data-testid="libro-page">
      <Seo
        title="Il mio libro — Io e il mio 18 | Laure Hila"
        description="Io e il mio 18 — La storia di mia figlia Antonella. Un libro nato dal desiderio di dare voce a una bambina speciale e a un amore che non finisce."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Book",
          name: "Io e il mio 18 — La storia di mia figlia Antonella",
          author: { "@type": "Person", name: "Laure Hila" },
          inLanguage: "it-IT",
          url: "https://amzn.eu/d/03Hf58U2",
        }}
      />

      <section className="pt-32 md:pt-44 pb-16 md:pb-24 grain relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <FadeIn>
              <p className="eyebrow mb-5">Il mio libro</p>
            </FadeIn>
            <h1 className="font-serif font-medium text-ink leading-[1.05] tracking-tight">
              <MaskedLine delay={0.1} className="text-5xl md:text-7xl">
                Io e il mio <span className="italic text-gold">18</span>
              </MaskedLine>
            </h1>
            <FadeIn delay={0.45}>
              <p className="mt-4 font-serif text-2xl md:text-3xl italic text-stone">
                La storia di mia figlia Antonella
              </p>
              <p className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-stone">
                Questo libro nasce dal desiderio di raccontare la storia di
                Antonella e di dare voce a una bambina speciale che ha vissuto
                soltanto due mesi e una settimana, ma che ha lasciato un'impronta
                indelebile nel cuore di chi l'ha amata.
              </p>
              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-stone">
                È una storia di dolore, ma anche di amore incondizionato, speranza,
                coraggio e fede. L'ho scritta perché il nome di Antonella non
                venisse dimenticato, per tutte le famiglie che hanno vissuto o
                stanno vivendo esperienze simili, e per chiunque voglia comprendere
                quanto amore possa esistere anche nel tempo più breve.
              </p>
              <p className="mt-5 max-w-xl font-serif text-xl italic text-ink">
                Perché alcune vite, anche se brevi, riescono a cambiare per sempre
                quelle degli altri.
              </p>
              <a
                href={LINKS.amazon}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="libro-cta-amazon"
                className="mt-9 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-ink text-cream font-semibold text-sm tracking-wide hover:bg-gold transition-colors duration-300 group"
              >
                Scopri il libro
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </FadeIn>
          </div>
          <Reveal className="md:col-span-5">
            <div className="relative mx-auto max-w-[300px] md:max-w-[340px]">
              <div className="absolute -inset-5 bg-gold/15 rounded-2xl rotate-3" />
              <div className="absolute -inset-5 border border-gold/40 rounded-2xl -rotate-2" />
              <img
                src={BOOK_COVER.url}
                alt={BOOK_COVER.alt}
                data-testid="libro-cover-image"
                className="relative z-10 w-full rounded-lg shadow-[0_35px_70px_rgba(44,42,41,0.25)]"
                loading="eager"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={IMAGES.libroMood.url}
                alt={IMAGES.libroMood.alt}
                className="w-full aspect-[16/10] md:aspect-[21/9] object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-ink/30 flex items-center justify-center px-6">
                <blockquote className="max-w-3xl text-center font-serif text-2xl md:text-4xl text-cream text-balance">
                  «Ho scritto queste pagine perché il nome di Antonella non venisse{" "}
                  <span className="italic text-gold">dimenticato</span>.»
                </blockquote>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
